/**
 * Audit Log Row Component
 * Displays a single audit log entry with expandable details
 * Shared between Admin and Provider portals
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useCascadeFadeIn } from "../../hooks/useCascadeFadeIn";
import { Badge } from "../ui/badge";
import { cn } from "../ui/cn";
import { Collapsible, CollapsibleContent } from "../ui/collapsible";
import { TableCell, TableRow } from "../ui/table";
import { type AuditLog, ACTION_LABELS, formatDateTime, getActionBadgeVariant } from "./types";

interface AuditLogRowProps {
  readonly log: AuditLog;
  readonly index?: number;
  /** Hide user column for provider view (they only see their own logs) */
  readonly hideUser?: boolean;
}

export function AuditLogRow({ log, index = 0, hideUser = false }: AuditLogRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { ref, isVisible } = useCascadeFadeIn<HTMLTableRowElement>({
    index,
    threshold: 0.1,
    staggerDelay: 30,
  });

  const colSpan = hideUser ? 5 : 6;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <>
        <TableRow
          ref={ref}
          className={cn(
            "cursor-pointer transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <TableCell className="w-[50px]">
            <div className="flex items-center justify-center">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </TableCell>
          <TableCell className="text-xs text-muted-foreground font-medium">{formatDateTime(log.createdAt)}</TableCell>
          {!hideUser && (
            <TableCell>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-white/80">{log.user.email}</span>
                {log.user.organization && <span className="text-xs text-white/80">{log.user.organization}</span>}
              </div>
            </TableCell>
          )}
          <TableCell>
            <Badge variant={getActionBadgeVariant(log.action)} className="font-medium">
              {ACTION_LABELS[log.action] || log.action}
            </Badge>
          </TableCell>
          <TableCell className="text-sm font-medium">
            {log.entityType && <span className="capitalize text-white/90">{log.entityType}</span>}
          </TableCell>
          <TableCell className="text-xs text-white/80 font-mono">{log.ipAddress || "—"}</TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <TableRow>
            <TableCell colSpan={colSpan} className="bg-black/0 py-4">
              <div className="space-y-3 max-w-4xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 text-sm">
                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                      Entity ID
                    </span>
                    <span className="font-mono text-xs bg-background/0 px-2 py-2">{log.entityId || "—"}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-white font-semibold uppercase tracking-wide">User Agent</span>
                    <span className="text-xs text-white bg-background/0 px-2 py-2 truncate">
                      {log.userAgent || "—"}
                    </span>
                  </div>
                </div>
                {log.metadata && (
                  <div className="flex flex-col gap-2 pt-6">
                    <span className="text-xs text-white font-semibold uppercase tracking-wide">Metadata</span>
                    <pre className="bg-background/0 p-3 text-xs overflow-x-auto text-white/80">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}
